import { useState, useEffect } from 'react';
import { ConfirmModal } from '@/src/modules/components/ui/ConfirmModal';
import { repriceCreditPack } from '../services/credit-pack.service';
import type { AdminCreditPackRow } from '../types/credit-pack.types';

/**
 * Wallet-pack floor, mirrored from the backend (wallet-pricing.ts, ADR-019): ₹500 minimum sale ÷ the cheapest template's
 * 800 credits. Cheaper than this and a partner buys the cheapest template under the minimum sale. The backend re-checks.
 */
export const WALLET_MIN_RUPEES_PER_CREDIT = 500 / 800;

interface Props {
  open: boolean;
  pack: AdminCreditPackRow | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreditPackRepriceModal({ open, pack, onClose, onSuccess }: Props) {
  const [rupees, setRupees] = useState<number>(0);
  const [credits, setCredits] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pack) {
      setRupees(pack.amountPaise / 100);
      setCredits(pack.credits);
      setError(null);
    }
  }, [pack, open]);

  if (!pack) return null;

  const handleSubmit = async () => {
    if (rupees <= 0) {
      setError('Price in ₹ must be greater than zero');
      return;
    }
    if (credits <= 0) {
      setError('Credits must be greater than zero');
      return;
    }
    if (isWallet && walletProblem) {
      setError(walletProblem);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await repriceCreditPack(pack.id, {
        amountPaise: Math.round(rupees * 100),
        credits,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reprice pack');
    } finally {
      setLoading(false);
    }
  };

  const isWallet = pack.unit === 'VALUE';
  // Wallet packs (1 credit = ₹1): the ratio IS the discount. The per-publish ₹17.4 cost model below applies to the
  // retired publish-count packs only.
  const rupeesPerCredit = credits > 0 ? rupees / credits : 0;
  const discountPercent = credits > 0 && credits > rupees ? ((credits - rupees) / credits) * 100 : 0;
  const walletProblem =
    rupeesPerCredit > 0 && rupeesPerCredit < WALLET_MIN_RUPEES_PER_CREDIT
      ? `Below the floor: max ${(100 - WALLET_MIN_RUPEES_PER_CREDIT * 100).toFixed(1)}% off, otherwise the cheapest template sells under the ₹500 minimum.`
      : rupeesPerCredit > 1
        ? 'More than ₹1 per credit is a markup, not a discount.'
        : null;
  const netPerCredit = credits > 0 ? (rupees / 1.18) / credits : 0;
  const marginPercent = netPerCredit > 0 ? ((netPerCredit - 17.4) / netPerCredit) * 100 : 0;

  return (
    <ConfirmModal
      open={open}
      title={`Reprice Pack — ${pack.displayName}`}
      description={`Currently ₹${(pack.amountPaise / 100).toLocaleString('en-IN')} for ${pack.credits} credits (version ${pack.version}). Repricing creates a new version; existing lots are unaffected.`}
      confirmLabel="Create New Version"
      loading={loading}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <div className="space-y-4 pt-2">
        {error && (
          <div className="p-3 text-xs rounded-lg bg-red-500/10 border border-red-500/20 text-red-500">
            {error}
          </div>
        )}
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            New Price (₹)
          </label>
          <input
            type="number"
            min="1"
            value={rupees || ''}
            onChange={(e) => setRupees(Number(e.target.value))}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1">
            Credits Granted
          </label>
          <input
            type="number"
            min="1"
            value={credits || ''}
            onChange={(e) => setCredits(Number(e.target.value))}
            className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30"
          />
        </div>
        {isWallet ? (
          <div className="p-3 rounded-lg bg-surface-container-low border border-border-subtle text-xs space-y-1" data-testid="reprice-wallet-summary">
            <div className="flex justify-between text-on-surface-variant">
              <span>Partner pays → gets:</span>
              <span className="font-semibold text-on-surface">₹{rupees.toLocaleString('en-IN')} → ₹{credits.toLocaleString('en-IN')} of wallet value</span>
            </div>
            <div className="flex justify-between items-center text-on-surface-variant pt-1 border-t border-border-subtle">
              <span>Discount (the pack ratio):</span>
              <span className={`font-bold ${walletProblem ? 'text-red-500' : 'text-emerald-500'}`}>{discountPercent.toFixed(1)}% off</span>
            </div>
            {walletProblem && <p className="text-red-500" role="alert">{walletProblem}</p>}
          </div>
        ) : (
        <div className="p-3 rounded-lg bg-surface-container-low border border-border-subtle text-xs space-y-1">
          <div className="flex justify-between text-on-surface-variant">
            <span>Net Price per Credit:</span>
            <span className="font-semibold text-on-surface">₹{netPerCredit.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-on-surface-variant">
            <span>Gross Price per Credit:</span>
            <span className="font-semibold text-on-surface">₹{(rupees / credits || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-on-surface-variant pt-1 border-t border-border-subtle">
            <span>Floor Margin (≥75%):</span>
            <span
              className={`font-bold ${
                marginPercent >= 75 ? 'text-emerald-500' : 'text-amber-500'
              }`}
            >
              {marginPercent.toFixed(1)}% {marginPercent < 75 && '(Below 75% target)'}
            </span>
          </div>
        </div>
        )}
      </div>
    </ConfirmModal>
  );
}
